import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { VEHICLE_TYPES } from '../lib/constants.js'
import { getSession, saveSession, clearSession } from '../lib/session.js'
import { pageBg as podiumBg } from '../lib/images'
import VehiclePicker from '../components/ui/VehiclePicker.jsx'

// ── Saisie du code ────────────────────────────────────────────
function CodeStep({ onConfirm }) {
  const [code, setCode]     = useState('')
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()

  useEffect(() => { inputRef.current?.focus() }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    const clean = code.trim().toUpperCase()
    if (clean.length !== 6) return setError('Le code fait 6 caractères.')
    setLoading(true)
    setError('')
    try {
      await apiFetch(`/api/games/${clean}`)
      onConfirm(clean)
    } catch {
      setError('Partie introuvable. Vérifie le code.')
    }
    setLoading(false)
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6 w-full flex-1">
      <div className="text-center">
        <div className="text-6xl mb-2">🏁</div>
        <h1 className="text-2xl font-semibold text-white">Rejoindre une partie</h1>
        <p className="text-blue-200 mt-1">Entre le code affiché par ton prof</p>
      </div>
      <form onSubmit={handleSubmit} className="w-full max-w-xs space-y-3">
        <input
          ref={inputRef}
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().slice(0, 6))}
          placeholder="ABC123"
          className="w-full text-center text-3xl font-mono font-semibold tracking-[0.3em] border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-4 focus:outline-none focus:border-white uppercase"
          maxLength={6}
          autoComplete="off"
          autoCapitalize="characters"
        />
        {error && <p className="text-red-300 text-sm text-center">{error}</p>}
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="w-full bg-white text-primary font-semibold py-3 rounded-2xl text-lg disabled:opacity-50 active:scale-95 transition"
        >
          {loading ? '…' : 'Entrer →'}
        </button>
      </form>
    </div>
  )
}

// ── Pseudo + choix du véhicule ────────────────────────────────
function ProfileStep({ code, onJoined }) {
  const [pseudo, setPseudo]   = useState('')
  const [vehicle, setVehicle] = useState(VEHICLE_TYPES[0].emoji)
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)
  const joiningRef = useRef(false)

  async function handleJoin(e) {
    e.preventDefault()
    if (!pseudo.trim()) return setError('Entre un pseudo.')
    if (joiningRef.current) return
    joiningRef.current = true
    setLoading(true)
    setError('')
    try {
      const { kart, session_token } = await apiFetch(`/api/karts/${code}/join`, {
        method: 'POST',
        body: JSON.stringify({ pseudo: pseudo.trim(), type_vehicule: vehicle }),
        retry: false,
      })
      saveSession(code, session_token, kart.id)
      onJoined()
    } catch (err) {
      setError(err.message)
      joiningRef.current = false
    }
    setLoading(false)
  }

  return (
    <form onSubmit={handleJoin} className="flex flex-col gap-3 w-full flex-1 min-h-0">
      <div className="text-center flex-shrink-0">
        <h2 className="text-xl font-semibold text-white">Crée ton pilote</h2>
      </div>

      {/* Pseudo */}
      <input
        value={pseudo}
        onChange={e => setPseudo(e.target.value.slice(0, 20))}
        placeholder="Ton pseudo…"
        autoFocus
        className="flex-shrink-0 w-full text-center text-xl font-semibold border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-3 focus:outline-none focus:border-white"
        maxLength={20}
      />

      {/* Sélecteur emoji */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <p className="text-white font-medium text-sm">Ton véhicule</p>
          <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1">
            <TwemojiImg emoji={vehicle} size={16} />
            <span className="text-white text-xs font-medium">
              {VEHICLE_TYPES.find(v => v.emoji === vehicle)?.label}
            </span>
          </div>
        </div>
        <VehiclePicker value={vehicle} onChange={setVehicle} />
      </div>

      {error && (
        <p className="flex-shrink-0 text-red-300 text-sm text-center bg-red-500/20 rounded-xl py-2 px-3">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading || !pseudo.trim()}
        className="flex-shrink-0 w-full bg-white text-primary font-semibold py-3 rounded-2xl text-lg disabled:opacity-50 active:scale-95 transition"
      >
        {loading ? 'Connexion…' : '🚦 Rejoindre la course !'}
      </button>
    </form>
  )
}

// ── Page principale ───────────────────────────────────────────
export default function JoinPage() {
  const { code: codeFromUrl } = useParams()
  const navigate = useNavigate()
  const [code, setCode] = useState(codeFromUrl?.toUpperCase() ?? null)

  useEffect(() => {
    if (!code) return
    const stored = getSession(code)
    if (!stored) return
    apiFetch(`/api/karts/${code}/me`, { headers: { 'x-session-token': stored.token } })
      .then(({ game }) => {
        if (game.statut === 'waiting') {
          navigate(`/lobby/${code}`, { replace: true })
        } else {
          navigate(`/game/${code}`, { replace: true })
        }
      })
      .catch(() => {
        clearSession(code)
      })
  }, [code, navigate])

  const bgStyle = { backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }

  return (
    <div className="h-[100dvh] flex flex-col px-4 pt-4 pb-3" style={bgStyle}>
      <div className="flex-1 min-h-0 w-full max-w-sm mx-auto flex flex-col">

        {/* Logo + titre — non figé, fait partie du flux */}
        <div className="flex-shrink-0 flex flex-col items-center pb-2">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-10 h-10 rounded-full object-cover mb-1" />
          <p className="text-white font-semibold text-base">Bienvenue sur KartQuiz</p>
        </div>

        <div className="flex-1 min-h-0 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-4 shadow-xl flex flex-col">
          {!code ? (
            <CodeStep onConfirm={c => setCode(c)} />
          ) : (
            <ProfileStep code={code} onJoined={() => navigate(`/lobby/${code}`)} />
          )}
        </div>
      </div>
    </div>
  )
}
